import React, { useState } from "react";
import moment from "moment";
import { Rosca, Barras, CORES, numero, pontos } from "../../Components/Graficos";
import { Painel, Card, Filtro, FiltroConcessionaria, TabelaDashboard, Abas, useDashboard, useFiltro, MESES, ANOS } from "./Painel";

const COR_STATUS = { Ativo: CORES[0], Inativo: CORES[2], "Pré-cadastro": CORES[1] };
const CABECALHO = {
  sx: { "& .Mui-TableHeadCell-Content-Wrapper": { whiteSpace: "normal", overflow: "visible", textOverflow: "clip", lineHeight: 1.2 } },
};
// rótulos curtos para caber no cabeçalho sem reticências
const CARGO_CURTO = { "Consultor de pós-vendas HMB": "Pós-vendas HMB", "Consultor de vendas HMB": "Vendas HMB" };
const VALOR = {
  size: 130,
  enableColumnActions: false,
  muiTableHeadCellProps: { align: "center", ...CABECALHO },
  muiTableBodyCellProps: { align: "center", sx: { color: "#002c5f", fontWeight: 600 } },
};

// Com mês selecionado agrupa por dia (1..N); sem mês, por MM/AAAA. `data` vem como "AAAA-MM-DDT00:00:00".
function agruparPeriodo(linhas, { ano, mes }, series, valorDe, serieDe = () => 0) {
  const grupos = new Map();
  const novo = () => series.map(() => 0);
  if (mes) {
    const dias = ano ? moment(`${ano}-${mes}`, "YYYY-M").daysInMonth() : 31;
    for (let d = 1; d <= dias; d++) grupos.set(String(d), novo());
  }
  linhas.forEach((l) => {
    const chave = mes ? String(Number(l.data.slice(8, 10))) : `${l.data.slice(5, 7)}/${l.data.slice(0, 4)}`;
    if (!grupos.has(chave)) grupos.set(chave, novo());
    const i = serieDe(l);
    if (i >= 0) grupos.get(chave)[i] += valorDe(l);
  });
  return [...grupos].map(([label, valores]) => ({ label, valores }));
}

const filtrosPeriodo = (filtro, set, concessionarias) => (
  <>
    <Filtro label="Ano" value={filtro.ano} onChange={set("ano")} opcoes={ANOS} />
    <Filtro label="Mês" value={filtro.mes} onChange={set("mes")} opcoes={MESES} />
    <FiltroConcessionaria value={filtro.dealerId} onChange={set("dealerId")} lista={concessionarias} />
  </>
);

function Participantes({ concessionarias }) {
  const [filtro, set] = useFiltro({ dealerId: "" });
  const { dados, atualizacao } = useDashboard("participantes", filtro);
  const porCargo = dados?.porCargo || [];
  const cargos = [...new Set(porCargo.map((p) => p.cargo))];

  return (
    <Painel
      titulo="Participantes"
      atualizacao={atualizacao}
      filtros={<FiltroConcessionaria value={filtro.dealerId} onChange={set("dealerId")} lista={concessionarias} />}
      exportar={{ path: "participantes/exportar", params: filtro, nome: "Dashboard participantes" }}
    >
      <div className="row g-3">
        <div className="col-md-3 d-flex flex-column gap-3">
          <Card valor={numero(dados?.cadastrados)} label="Cadastrados" />
          <Card valor={numero(dados?.ativos)} label="Ativos" />
          <Card valor={`${numero(dados?.percentualAtivos)}%`} label="% Ativos" />
        </div>
        <div className="col-md-9">
          <div className="row g-3">
            {cargos.length === 0 && (
              <div className="col-md-6">
                <Rosca titulo="Participantes" dados={[]} />
              </div>
            )}
            {cargos.map((cargo) => (
              <div className="col-md-6" key={cargo}>
                <Rosca
                  titulo={cargo}
                  dados={Object.keys(COR_STATUS).map((status) => ({
                    label: status,
                    color: COR_STATUS[status],
                    value: porCargo.find((p) => p.cargo === cargo && p.status === status)?.quantidade || 0,
                  }))}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Painel>
  );
}

function Concessionarias({ concessionarias }) {
  const [filtro, set] = useFiltro({ dealerId: "" });
  const { dados, atualizacao } = useDashboard("concessionarias/participantes", filtro);
  const lista = dados || [];
  const cargos = [...new Set(lista.flatMap((c) => c.cargos.map((x) => x.cargo)))];
  const colunas = [
    { accessorKey: "nomeFantasia", header: "Concessionária", size: 220, enableColumnActions: false, muiTableHeadCellProps: CABECALHO },
    ...cargos.map((cargo) => ({
      id: cargo,
      header: `${CARGO_CURTO[cargo] ?? cargo} ativos`,
      accessorFn: (c) => {
        const x = c.cargos.find((y) => y.cargo === cargo);
        return x ? `${x.ativos}/${x.total}` : "-";
      },
      ...VALOR,
    })),
    { id: "totalAtivos", header: "Total ativos", accessorFn: (c) => `${c.ativos}/${c.total}`, ...VALOR },
  ];

  return (
    <Painel
      titulo="Concessionárias"
      atualizacao={atualizacao}
      filtros={<FiltroConcessionaria value={filtro.dealerId} onChange={set("dealerId")} lista={concessionarias} />}
      exportar={{ path: "concessionarias/participantes/exportar", params: filtro, nome: "Dashboard concessionarias" }}
    >
      {/* key: o material-react-table fixa a ordem das colunas no 1º render; recria quando os cargos mudam */}
      <TabelaDashboard key={cargos.join("|")} colunas={colunas} dados={lista} />
    </Painel>
  );
}

function LogAcessos({ concessionarias }) {
  const [filtro, set] = useFiltro({ ano: new Date().getFullYear(), mes: new Date().getMonth() + 1, dealerId: "" });
  const { dados, atualizacao } = useDashboard("log-acessos", filtro);
  const porCargo = dados?.porCargo || [];
  const series = porCargo.map((c, i) => ({ nome: c.cargo, cor: CORES[i % CORES.length] }));
  const porPeriodo = agruparPeriodo(dados?.porDia || [], filtro, series, (l) => l.acessos, (l) =>
    series.findIndex((s) => s.nome === l.cargo)
  );

  return (
    <Painel
      titulo="Log de Acessos"
      atualizacao={atualizacao}
      filtros={filtrosPeriodo(filtro, set, concessionarias)}
      exportar={{ path: "log-acessos/exportar", params: filtro, nome: "Dashboard log de acessos" }}
    >
      <div className="row g-3 mb-3">
        <div className="col-6 col-md">
          <Card valor={numero(dados?.totalAcessos)} label="Total de acessos" />
        </div>
        {porCargo.map((c) => (
          <div className="col-6 col-md" key={c.cargo}>
            <Card valor={numero(c.acessos)} label={`Acesso - ${c.cargo}`} />
          </div>
        ))}
        <div className="col-6 col-md">
          <Card valor={numero(dados?.mediaAcessosDia)} label="Média de acessos/dia" />
        </div>
        <div className="col-6 col-md">
          <Card valor={numero(dados?.acessosUnicos)} label="Acessos únicos" />
        </div>
      </div>
      <div className="row g-3">
        <div className="col-md-8">
          <Barras titulo={filtro.mes ? "Acessos por dia" : "Acessos por mês"} dados={porPeriodo} series={series} />
        </div>
        <div className="col-md-4">
          <Rosca
            titulo="Acessos únicos"
            dados={porCargo.map((c, i) => ({ label: c.cargo, value: c.acessosUnicos, color: series[i].cor }))}
          />
        </div>
      </div>
    </Painel>
  );
}

function FaleConosco({ concessionarias }) {
  const [filtro, set] = useFiltro({ ano: "", mes: "", dealerId: "" });
  const { dados, atualizacao } = useDashboard("fale-conosco", filtro);
  const tempo = dados?.tempoMedioRespostaDias;

  return (
    <Painel
      titulo="Fale Conosco"
      atualizacao={atualizacao}
      filtros={filtrosPeriodo(filtro, set, concessionarias)}
      exportar={{ path: "fale-conosco/exportar", params: filtro, nome: "Dashboard fale conosco" }}
    >
      <div className="row g-3 mb-3">
        <div className="col-6 col-md-3">
          <Card valor={numero(dados?.totalChamados)} label="Total de chamados" />
        </div>
        <div className="col-6 col-md-3">
          <Card valor={numero(dados?.mediaChamadosDia)} label="Média de chamados/dia" />
        </div>
        <div className="col-6 col-md-3">
          <Card
            valor={tempo == null ? "-" : `${numero(tempo)} dias`}
            label="Tempo médio de resposta"
            title="Tempo médio entre a abertura e o encerramento do chamado"
          />
        </div>
        <div className="col-6 col-md-3">
          <Card valor={dados?.principalMotivo || "-"} label="Principal motivo de contato" />
        </div>
      </div>
      <div className="row g-3">
        <div className="col-md-7">
          <Barras
            titulo={filtro.mes ? "Chamados por dia" : "Chamados por mês"}
            dados={agruparPeriodo(dados?.porDia || [], filtro, [0], (l) => l.chamados)}
          />
        </div>
        <div className="col-md-5">
          <Barras
            horizontal
            espacado
            titulo="Motivos de contato"
            dados={(dados?.motivos || []).map((m) => ({ label: m.motivo, valores: [m.chamados] }))}
          />
        </div>
      </div>
    </Painel>
  );
}

function Campanha() {
  const [filtro, setFiltro] = useState({ ano: "", campanhaId: "" });
  const { dados: anos } = useDashboard("campanha/anos");
  const { dados: campanhas } = useDashboard("campanhas", { ano: filtro.ano });
  const { dados, atualizacao } = useDashboard("campanha", filtro);
  const porCargo = dados?.porCargo || [];

  return (
    <Painel
      titulo="Campanha"
      atualizacao={atualizacao}
      filtros={
        <>
          <Filtro
            label="Ano"
            value={filtro.ano}
            onChange={(ano) => setFiltro({ ano, campanhaId: "" })}
            opcoes={(anos || []).map((a) => ({ value: a, label: a }))}
          />
          <Filtro
            label="Campanha"
            todos="Todas"
            value={filtro.campanhaId}
            onChange={(campanhaId) => setFiltro((f) => ({ ...f, campanhaId }))}
            opcoes={(campanhas || []).map((c) => ({ value: c.campanhaId, label: c.nome }))}
          />
        </>
      }
      exportar={{ path: "campanha/exportar", params: filtro, nome: "Dashboard campanha" }}
    >
      <div className="row g-3">
        <div className="col-md-2 d-flex flex-column gap-3">
          <Card valor={numero(dados?.totalVendas)} label="Total de vendas" />
          <Card valor={pontos(dados?.pontosGanhos)} label="Pontos ganhos" />
        </div>
        <div className="col-md-5">
          <Rosca
            titulo="Performance participantes"
            dados={[
              { label: "Pontuaram", value: dados?.participantesPontuaram || 0, color: CORES[0] },
              { label: "Não pontuaram", value: dados?.participantesNaoPontuaram || 0, color: CORES[1] },
            ]}
          />
        </div>
        <div className="col-md-5">
          <Barras
            titulo="Performance x cargos"
            dados={porCargo.map((c) => ({ label: c.cargo, valores: [c.participantesPontuaram] }))}
          />
        </div>
      </div>
    </Painel>
  );
}

const ABAS = [
  ["Participantes", Participantes],
  ["Concessionárias", Concessionarias],
  ["Log de Acessos", LogAcessos],
  ["Fale Conosco", FaleConosco],
  ["Campanha", Campanha],
];

export default function Geral() {
  const [aba, setAba] = useState(0);
  const { dados: concessionarias } = useDashboard("concessionarias");
  const Aba = ABAS[aba][1];

  return (
    <div className="row">
      <span className="tituloPagina">DASHBOARD</span>
      <Abas abas={ABAS.map((a) => a[0])} aba={aba} onChange={setAba} />
      <Aba concessionarias={concessionarias || []} />
    </div>
  );
}
